import { Chip, InputAdornment, TextField } from "@mui/material";

/**
 * E15 — canonical billing-increment input (mirrors server-side range 1–3600,
 * enforced by parse_increment in app/core/rate_boundary.py).
 *
 * Preset chips (1s / 6s / 30s / 60s — the wholesale conventions) give
 * one-click entry; the numeric field still accepts any custom value in
 * the canonical range. Kept as ONE shared component so every surface
 * (supplier, rate-plan, reseller) presents increments identically.
 */
export const INCREMENT_PRESETS = [1, 6, 30, 60];

interface Props {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  disabled?: boolean;
}

export default function IncrementField({ value, onChange, label = "Billing Increment (seconds)", disabled }: Props) {
  const n = parseInt(value || "1", 10);
  const preset = INCREMENT_PRESETS.includes(n);
  return (
    <TextField
      type="number"
      label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min: 1, max: 3600, step: 1 }}
      helperText={
        preset
          ? `Presets below — calls round up to ${n}s blocks`
          : "Custom — 1–3600 s; calls round up to this many seconds"
      }
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {INCREMENT_PRESETS.map((p) => (
              <Chip
                key={p}
                label={`${p}s`}
                size="small"
                disabled={disabled}
                clickable
                color={n === p ? "primary" : "default"}
                onClick={() => onChange(String(p))}
                sx={{ mr: 0.5 }}
              />
            ))}
          </InputAdornment>
        ),
      }}
    />
  );
}
